/**
 * Shared plumbing for export dialogs that remember what the user picked last
 * time (STAF, NSD).
 *
 * Each dialog owns its own preferences key, its own defaults and its own
 * field-level guards; this module only holds the parts that are identical
 * between them. The storage shape follows the other "remember what I picked"
 * dialogs in the app - one JSON object under one namespaced key, as
 * `cgHelpers-copyBayCGs` and `bayHelpers-copyBayData` do.
 */
import { getPreferencesValue, setPreferencesKeyAndValue } from "@tedivo/tedivo-preferences";

/**
 * The raw object stored under `key`, or `{}` when there is nothing usable.
 *
 * Callers must validate every field they read from it: localStorage outlives
 * releases, so a stored value can be stale (an option we no longer offer) or
 * inapplicable to the file being exported. A corrupt entry yields `{}` rather
 * than throwing, so a bad preference can never stop a dialog from opening.
 */
export function readSavedExportOptions<T>(key: string): Partial<T> {
  try {
    // Objects come back from the store as a raw string - only arrays are parsed
    // for us - so this is the one place that has to parse it.
    return JSON.parse((getPreferencesValue(key) as string) || "{}");
  } catch {
    return {};
  }
}

/** Remembers the options for the next export. Call only after a successful
 *  export, never on cancel, as the other dialogs that remember do. */
export function saveExportOptions<T extends object>(key: string, options: T): void {
  setPreferencesKeyAndValue(key, options);
}

/**
 * `value` when it is one of `allowed`, else `fallback`.
 *
 * Membership, never truthiness: a stored `0` or `false` is a real choice that a
 * `||` fallback would silently replace with the default.
 */
export function pickAllowed<T>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

/** The store decodes booleans on its own, but not always - a value written by an
 *  older build can still come back as the string. */
export function isTrue(value: unknown): boolean {
  return value === true || value === "true";
}

/** Reads a remembered boolean that defaults to ON, so only an explicitly stored
 *  `false` turns it off. */
export function isNotFalse(value: unknown): boolean {
  return !(value === false || value === "false");
}
